import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';
import { BasketActions, BasketActionTypes, BasketLoadingItem, LoadingItemTypes } from '../basket.types';
import { ACTION_CREATOR_NAME, userHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { AppState, ErrorAction } from '../../root.types';
import { BasketItemModel, ProductTypes, BasketDetailsModel, SOFASHIELD } from 'models/Basket';
import { api } from 'repositories/api';
import { initBasketPageSaga } from './initBasketPage';
import { callBasketSaga, BasketSagaReturnType } from 'store/sagaHelpers';

export interface UpdateSofashieldAction extends Action<BasketActionTypes.UPDATE_SOFASHIELD> {
    itemProductCode: string;
    sofashieldQuantity: number;
}

export interface UpdateSofashieldActionStart extends Action<BasketActionTypes.UPDATE_SOFASHIELD_START> {
    loadingItem: BasketLoadingItem;
    sofashieldQuantity: number;
}

export interface UpdateSofashieldActionSuccess extends Action<BasketActionTypes.UPDATE_SOFASHIELD_SUCCESS> {
    loadingItem: BasketLoadingItem;
    sofashieldQuantity: number;
}

export interface UpdateSofashieldActionError extends ErrorAction<BasketActionTypes.UPDATE_SOFASHIELD_ERROR> {
    loadingItem: BasketLoadingItem;
    sofashieldQuantity: number;
}

export const updateSofashieldQuantity: (itemProductCode: string, sofashieldQuantity: number) => UpdateSofashieldAction = (itemProductCode: string, sofashieldQuantity: number) => ({
    type: BasketActionTypes.UPDATE_SOFASHIELD,
    itemProductCode,
    sofashieldQuantity
});
updateSofashieldQuantity[ACTION_CREATOR_NAME] = 'updateSofashieldQuantity';

export type UpdateSofashieldQuantityFunc = (...args: Parameters<typeof updateSofashieldQuantity>) => void;

export function* updateSofashieldSaga({ itemProductCode, sofashieldQuantity }: UpdateSofashieldAction) {
    yield put<BasketActions>({
        type: BasketActionTypes.UPDATE_SOFASHIELD_START,
        loadingItem: { id: itemProductCode, loadingType: LoadingItemTypes.ChangeSofashieldQuantity },
        sofashieldQuantity
    });
}

export function* updateSofashieldQueueSaga({ loadingItem, sofashieldQuantity }: UpdateSofashieldActionStart): BasketSagaReturnType {
    try {
        let state: AppState = yield select();
        const basketId = state.basketState.basket!.guid;
        let entryItem = state.basketState.basket!.entries?.find((entry: BasketItemModel) => entry.product.code === loadingItem.id);
        let entryNumber = entryItem?.entryNumber;

        if (entryNumber !== undefined) {
            yield api.hybris.updateSofashieldQuantity(
                userHelper.getCurrentUserName(),
                basketId,
                entryNumber,
                entryItem!.quantity,
                sofashieldQuantity
            );

            const sofashieldItem = state.basketState.basket!.entries.find(e => e.product.name === SOFASHIELD);

            yield callBasketSaga(initBasketPageSaga, {
                type: BasketActionTypes.INIT_BASKET_PAGE
            });

            state = yield select();
            sendAnalytics(sofashieldItem?.product.code ?? SOFASHIELD, entryItem!.sofashieldQuantity, sofashieldQuantity, sofashieldItem, state.basketState.basket!);

            yield put<BasketActions>({
                type: BasketActionTypes.UPDATE_SOFASHIELD_SUCCESS,
                loadingItem,
                sofashieldQuantity
            });
        }

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.UPDATE_SOFASHIELD_ERROR,
            loadingItem,
            sofashieldQuantity,
            error: errorToBasketErrorModel(error, 'updateSofashieldQueueSaga')
        });

        return false;
    }
}

function sendAnalytics(code: string, prevQuantity: number, newQuantity: number, sofashieldItem: BasketItemModel | undefined, updatedBasket: BasketDetailsModel): void {
    analyticsHelper.withLogging(() => {
        const itemsCountDifference = newQuantity - prevQuantity;

        if (itemsCountDifference === 0) {
            return;
        }

        if (itemsCountDifference > 0) {
            api.google.addAdditionalProduct(ProductTypes.Sofashield, code, Math.abs(itemsCountDifference));
            api.exponea.addToCart(updatedBasket, sofashieldItem!, Math.abs(itemsCountDifference));
        } else {
            api.google.removeAdditionalProduct(ProductTypes.Sofashield, code, Math.abs(itemsCountDifference));
            api.exponea.removeFromCart(updatedBasket, sofashieldItem!, Math.abs(itemsCountDifference));
        }
    }, 'updateSofashieldQueueSaga');
}
